import React, { forwardRef, useCallback, useState } from 'react';
import { LinearGradient as ExpoLinearGradient, } from 'expo-linear-gradient';
// react-native-linear-gradient's defaults
const DEFAULT_START = { x: 0.5, y: 0 };
const DEFAULT_END = { x: 0.5, y: 1 };
const DEFAULT_ANGLE = 45;
const DEFAULT_ANGLE_CENTER = { x: 0.5, y: 0.5 };
// Math.cos(Math.PI / 2) is 6e-17, not 0
const trig = (value) => (Math.abs(value) < 1e-9 ? 0 : value);
const needsSize = (angle) => {
    const radians = (angle * Math.PI) / 180;
    return trig(Math.sin(radians)) !== 0 && trig(Math.cos(radians)) !== 0;
};
/**
 * expo-linear-gradient only takes start and end points, as fractions of the
 * view's size. react-native-linear-gradient drew `angle` the way CSS does: the
 * gradient line runs through `angleCenter`, and is as long as the view's
 * projection onto it, so the corners nearest its ends get the first and last
 * colours. These are the points it computed in pixels, as fractions.
 *
 * Multiples of 90° don't depend on the size. Other angles do, because the same
 * fractions point in a different direction when the view isn't square; without
 * a size, a square is assumed.
 */
export const pointsFromAngle = (angle, center = DEFAULT_ANGLE_CENTER, size) => {
    const radians = (angle * Math.PI) / 180;
    const sin = trig(Math.sin(radians));
    const cos = trig(Math.cos(radians));
    const { width, height } = size && size.width > 0 && size.height > 0 ? size : { width: 1, height: 1 };
    const halfLength = (Math.abs(width * sin) + Math.abs(height * cos)) / 2;
    const dx = (halfLength * sin) / width;
    const dy = (-halfLength * cos) / height;
    return {
        start: { x: center.x - dx, y: center.y - dy },
        end: { x: center.x + dx, y: center.y + dy },
    };
};
const LinearGradient = forwardRef(({ colors, locations, start = DEFAULT_START, end = DEFAULT_END, useAngle, angle = DEFAULT_ANGLE, angleCenter, onLayout, ...props }, ref) => {
    const [size, setSize] = useState();
    const measure = useAngle && needsSize(angle);
    const onLayoutMeasured = useCallback((event) => {
        const { width, height } = event.nativeEvent.layout;
        setSize((previous) => previous?.width === width && previous?.height === height
            ? previous
            : { width, height });
        onLayout?.(event);
    }, [onLayout]);
    const points = useAngle
        ? pointsFromAngle(angle, angleCenter, size)
        : { start, end };
    return (<ExpoLinearGradient ref={ref} {...props} colors={colors} locations={locations} start={points.start} end={points.end} 
    // react-native-linear-gradient didn't dither on Android
    dither={false} onLayout={measure ? onLayoutMeasured : onLayout}/>);
});
export default LinearGradient;
